import { IScope } from "angular";
import { StateService, TransitionService } from "@uirouter/angularjs";
import { IModalService, IModalStackService } from "angular-ui-bootstrap";
import { UserProfileService } from "@gtmhub/users/userProfile/profileService";
import { IProfile } from "@webapp/users/models/users.models";
import { IMasterplan } from "../models/models";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { InstallerService } from "../services/installerServices";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IConnectorBlueprint } from "@gtmhub/connectors/models";
import { storage } from "@gtmhub/core/storage";

export interface IWelcomeScope extends IScope {
  indicators: {
    loading: boolean;
    installing: boolean;
    uninstalling: boolean;
  };
  profile: IProfile;
  masterplans: IMasterplan[];
  selectedMasterplan: IMasterplan;
  install(masterplan: IMasterplan): void;
  uninstall(masterplan: IMasterplan): void;
  skip(): void;
  changeSelection(masterplan: IMasterplan): void;
}

export class WelcomeCtrl {
  public static $inject = [
    "$scope",
    "$rootScope",
    "$transitions",
    "$state",
    "UserProfileService",
    "InstallerService",
    "AccountService",
    "$uibModal",
    "$uibModalStack",
    "UIErrorHandlingService",
  ];

  constructor(
    private $scope: IWelcomeScope,
    private $rootScope: IGtmhubRootScopeService,
    private $transitions: TransitionService,
    private $state: StateService,
    private profileService: UserProfileService,
    private installerService: InstallerService,
    private accountService: AccountService,
    private $uibModal: IModalService,
    private $uibModalStack: IModalStackService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.init();
  }

  private hiddenMasterPlans: string[] = ["xero"];

  private init = () => {
    this.$scope.indicators = {
      loading: false,
      installing: false,
      uninstalling: false,
    };

    this.$scope.masterplans = [];
    this.$rootScope.noNavigation = true;
    this.$scope.changeSelection = this.changeSelection;
    this.$scope.profile = this.profileService.getProfile();

    this.$scope.install = this.install;
    this.$scope.uninstall = this.uninstall;
    this.$scope.skip = this.skip;

    this.$scope.$on(
      "$destroy",
      this.$transitions.onSuccess({}, (trans) => {
        const params = trans.router.globals.params;
        if (trans.router.globals.current.name === "gtmhub.welcome") {
          const plan = storage.get("selectedPlan");
          if (params.cancelConnect && plan) {
            storage.set("selectedPlan", null);
          } else if (plan) {
            storage.set("selectedPlan", null);
            this.install();
          }
        }
      })
    );

    this.loadMasterplans();
  };

  private loadMasterplans = () => {
    this.$scope.indicators.loading = true;

    this.installerService.getMasterplans().then(
      (masterplans) => {
        this.$scope.indicators.loading = false;

        // removing the hidden masterplans in UI
        this.$scope.masterplans = masterplans.filter((masterplan) => this.hiddenMasterPlans.indexOf(masterplan.name) === -1);

        this.updateSelection();
      },
      (err) => this.handleIndicatorError(err, "loading")
    );
  };

  private install = () => {
    if (this.$scope.selectedMasterplan == null) {
      return;
    }

    storage.set("selectedPlan", this.$scope.selectedMasterplan);

    const plans: string[] = [this.$scope.selectedMasterplan.name];

    this.$scope.indicators.installing = true;

    // make sure all connectors are installed
    this.installerService.getRequiredConnectors(plans).then(
      (connectorBlueprints: IConnectorBlueprint[]) => {
        // if there is no connectors, start installing masterplans
        if (connectorBlueprints.length === 0) {
          this.installerService.install(plans).then(
            () => {
              this.$scope.indicators.installing = false;

              this.accountService.setAccountSetting("skipWelcome", true);
              storage.set("selectedPlan", null);
              this.$uibModalStack.dismissAll();
              this.$state.go("gtmhub.insightboards");
            },
            (err) => this.handleIndicatorError(err, "installing", true)
          );
        } else {
          // connect the first connector
          this.$scope.indicators.installing = false;

          const connectorBlueprint: IConnectorBlueprint = connectorBlueprints[0];
          this.$state.go("gtmhub.welcome.installer.configureDataSource", {
            dataSourceBlueprintName: connectorBlueprint.name,
          });
        }
      },
      (err) => this.handleIndicatorError(err, "installing", true)
    );
  };

  private uninstall = (masterplan: IMasterplan) => {
    if (masterplan == null) {
      return;
    }

    this.$scope.indicators.uninstalling = true;

    this.installerService.uninstall(masterplan.id).then(
      () => {
        this.$scope.selectedMasterplan.installed = false;
        this.$scope.selectedMasterplan.selected = false;
        this.$scope.selectedMasterplan = null;

        this.$uibModal.open({
          templateUrl: require("../views/confirmation.jade"),
          windowClass: "medium-small",
          controller: [
            "$scope",
            "$uibModalInstance",
            function ($scope, $uibModalInstance) {
              $scope.close = () => {
                $uibModalInstance.close();
              };
            },
          ],
        });
      },
      (err) => this.handleIndicatorError(err, "uninstalling", true)
    );
  };

  private updateSelection = () => {
    const plan = storage.get<IMasterplan>("selectedPlan");
    if (plan != null) {
      for (let i = 0; i < this.$scope.masterplans.length; i++) {
        if (this.$scope.masterplans[i].name === plan.name) {
          this.$scope.masterplans[i].selected = true;
          break;
        }
      }
    }
  };

  private skip = () => {
    this.accountService.setAccountSetting("skipWelcome", true);
    storage.set("selectedPlan", null);
    this.$state.go("gtmhub.insightboards");
  };

  private changeSelection = (masterplan: IMasterplan) => {
    if (this.$scope.selectedMasterplan != null) {
      this.$scope.selectedMasterplan.selected = false;
    }
    masterplan.selected = true;
    this.$scope.selectedMasterplan = masterplan;
  };

  private handleIndicatorError = <E>(err: E, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };
}
